import React, {FC, RefObject, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {hasFocus, isElement} from '../../../../../libs/shared/helpers/src/browser/domHelpers';
import {KeyboardActionsEnum} from '../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {
  ClickEventHandler,
  FrontClickEvent,
  MouseEventHandler,
} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {KeyboardEventHandlers} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {ElectronPoint, openApplicationMenu} from '../../../../services/electron/electronBridgeOutbound';
import {
  getCanHideWindowsTitleBar,
  getIsApplicationMenuOpen,
} from '../../../../store/electron/electronSelectors';
import {TopBarButton} from '../topBarButton';

/*
 * Props.
 */

interface WindowsTitleBarMenuButtonProps {
  className?: string;
}

/*
 * Style.
 */

const WrapperDiv = styled.div`
  padding-right: 8px;
  margin-right: 8px;

  border-right: 1px solid ${(p) => p.theme.alphas.white20};
`;

/*
 * Component.
 */

export const WindowsTitleBarMenuButton: FC<WindowsTitleBarMenuButtonProps> = ({className}) => {
  const canHideWindowsTitleBar = useAppSelector(getCanHideWindowsTitleBar);
  const isApplicationMenuOpen = useAppSelector(getIsApplicationMenuOpen);
  const wasMenuOpenOnMouseDownRef = useRef(false);
  const [isFocused, setIsFocused] = useState(false);

  const buttonRef = useRef<HTMLDivElement>(null);
  useFocusEffect(buttonRef, isFocused);

  // If we are not running in a compatible desktop app, render nothing.
  if (!canHideWindowsTitleBar) {
    return null;
  }

  // If we are not focused and the menu is closed, render only the keyboard shortcut listener.
  const isVisible = isFocused || isApplicationMenuOpen;

  const onFocus = () => setIsFocused(true);
  const onBlur = () => setIsFocused(false);
  const onFocusToggle = () => setIsFocused((prevIsFocused) => !prevIsFocused);

  const maybeOpenMenuForEvent: ClickEventHandler = (event) => {
    // If the menu is already open, allow this event to potentially close the menu.
    const wasMenuOpenOnMouseDown = wasMenuOpenOnMouseDownRef.current;
    if (isApplicationMenuOpen || wasMenuOpenOnMouseDown) {
      return;
    }

    const cursorOffset = findCursorOffset(event);
    if (!cursorOffset) {
      return;
    }

    openApplicationMenu(cursorOffset);
  };

  const onMouseDown: MouseEventHandler = (event) => {
    wasMenuOpenOnMouseDownRef.current = isApplicationMenuOpen;
    maybeOpenMenuForEvent(event);
  };

  const onClick: ClickEventHandler = (event) => {
    maybeOpenMenuForEvent(event);
  };

  const handlers: KeyboardEventHandlers = {
    [KeyboardActionsEnum.FOCUS_APPLICATION_MENU]: onFocusToggle,
  };

  return (
    <KeyboardShortcuts handlers={handlers}>
      {isVisible && (
        <WrapperDiv className={className}>
          <TopBarButton
            ref={buttonRef}
            isSelected={isApplicationMenuOpen}
            onFocus={onFocus}
            onBlur={onBlur}
            // Open the menu on mouse down so you can drag and mouse up to trigger a menu item.
            onMouseDown={onMouseDown}
            // Open the menu on click also for accessibility.
            onClick={onClick}
            iconName="menu"
          />
        </WrapperDiv>
      )}
    </KeyboardShortcuts>
  );
};

/*
 * Hooks.
 */

/** Reconciles the DOM element focus state with the component focus state. */
function useFocusEffect(elementRef: RefObject<HTMLElement>, isFocused: boolean) {
  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    const isElementFocused = hasFocus(element);
    if (isFocused === isElementFocused) {
      return;
    }

    if (isFocused) {
      element.focus();
    } else {
      element.blur();
    }
  }, [elementRef, isFocused]);
}

/*
 * Helpers.
 */

function findCursorOffset(event: FrontClickEvent): ElectronPoint | undefined {
  if ('clientX' in event) {
    return {x: event.clientX, y: event.clientY};
  }

  if (isElement(event.target)) {
    return findRectCenter(event.target.getBoundingClientRect());
  }

  return undefined;
}

function findRectCenter(rect: DOMRect): ElectronPoint {
  return {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
}
